/**
 * News Tab Component
 * 
 * Monitor industry news, Federal Register alerts, and Executive Orders.
 * Extracted from IndustryNewsPage.tsx for use in unified External Data Dashboard.
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, EmptyState, Tabs } from '@tracekit/core';
import { useWorkspace } from '@tracekit/core';
import {
  Bell,
  RefreshCw,
  Clock,
  FileText,
  Newspaper,
  Scale,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  ChevronDown,
  ChevronUp,
  Settings,
  Rss,
} from 'lucide-react';
import {
  fetchExecutiveOrders,
  fetchFederalRegisterDocuments,
  fetchRSSFeeds,
  ExecutiveOrder,
  FederalRegisterDocument,
  RSSFeedItem,
} from '../../services/usGovernmentIntegrations';
import { DataSourceConfig } from '../../services/dataSourceIntegrations';
import { RSSFeedSelector } from '../RSSFeedSelector';
import { RSS_FEED_REGISTRY } from '../../services/rssFeedRegistry';
import { useExternalData } from '../../contexts/ExternalDataContext';

export function NewsTab() {
  const { projectId } = useWorkspace();
  const { 
    selectedZone,
    selectedKeywords,
    setSelectedKeywords,
  } = useExternalData();
  
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(300000); // 5 minutes
  const [newsCarouselIndex, setNewsCarouselIndex] = useState(0);
  const [federalCarouselIndex, setFederalCarouselIndex] = useState(0);
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  const [eoPage, setEoPage] = useState<number>(1);
  const [eoFilters, setEoFilters] = useState({ year: '2024', president: 'all' });
  
  // RSS Feed Selection
  const [selectedFeedIds, setSelectedFeedIds] = useState<string[]>(() => {
    const saved = localStorage.getItem('rfp-selected-feeds');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return RSS_FEED_REGISTRY.filter(f => f.priority <= 2).map(f => f.id);
      }
    }
    return RSS_FEED_REGISTRY.filter(f => f.priority <= 2).map(f => f.id);
  });
  
  // Save feed selection to localStorage
  useEffect(() => {
    localStorage.setItem('rfp-selected-feeds', JSON.stringify(selectedFeedIds));
  }, [selectedFeedIds]);
  
  // Base config for data fetching
  const baseConfig: DataSourceConfig = {
    enabled: true,
    sync_frequency: 'hourly',
    filters: {
      keywords: selectedKeywords,
      feedIds: selectedFeedIds,
    },
  };
  
  // Fetch RSS feeds
  const { data: rssItems = [], isLoading: loadingRSS, refetch: refetchRSS } = useQuery({
    queryKey: ['rss-feeds', projectId, selectedKeywords, selectedZone, selectedFeedIds],
    queryFn: () => fetchRSSFeeds(baseConfig, projectId || ''),
    enabled: !!projectId && selectedZone === 'usa',
    refetchInterval: autoRefresh ? refreshInterval : false,
  });
  
  // Fetch Federal Register alerts
  const { data: federalAlerts = [], isLoading: loadingFederal, refetch: refetchFederal } = useQuery({
    queryKey: ['federal-register-alerts', projectId, selectedKeywords],
    queryFn: () => fetchFederalRegisterDocuments(baseConfig, projectId || ''),
    enabled: !!projectId && selectedZone === 'usa' && autoRefresh,
    refetchInterval: autoRefresh ? refreshInterval : false,
  });
  
  // Fetch Executive Orders
  const { data: executiveOrders = [], isLoading: loadingEOs } = useQuery({
    queryKey: ['executive-orders', projectId, selectedKeywords],
    queryFn: () => fetchExecutiveOrders(baseConfig, projectId || ''),
    enabled: !!projectId && selectedZone === 'usa',
  });
  
  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refetchRSS();
      refetchFederal();
    }, refreshInterval);
    
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refetchRSS, refetchFederal]);
  
  if (!projectId) {
    return (
      <EmptyState
        icon={<Newspaper className="h-12 w-12" />}
        title="No Project Selected"
        description="Please select a project to view industry news and policy feeds"
      />
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Industry News & Policy Feed</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {autoRefresh ? 'Pause' : 'Resume'}
          </Button>
          <Button variant="primary" size="sm" onClick={() => { refetchRSS(); refetchFederal(); }}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Collapsible Alert Settings */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setAlertSettingsOpen(!alertSettingsOpen)}
            className="flex-1 flex items-center gap-2 hover:bg-gray-50 transition-colors rounded-lg p-2 -m-2"
          >
            <Settings className="h-5 w-5 text-gray-600" />
            <h4 className="text-lg font-semibold">Alert Settings</h4>
            <div className="ml-auto">
              {alertSettingsOpen ? (
                <ChevronUp className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-600" />
              )}
            </div>
          </button>
          <Button 
            variant="primary" 
            size="sm" 
            className="ml-4"
            onClick={() => {
              localStorage.setItem('rfp-selected-feeds', JSON.stringify(selectedFeedIds));
              localStorage.setItem('rfp-selected-keywords', JSON.stringify(selectedKeywords));
              alert('Settings saved!');
            }}
          >
            Save
          </Button>
        </div>
        
        {alertSettingsOpen && (
          <div className="px-4 pb-4 space-y-6 border-t pt-4 mt-4">
            {/* RSS Feed Selection */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Rss className="h-5 w-5 text-orange-600" />
                <label className="block text-sm font-medium">RSS Feed Sources</label>
                <Badge variant="primary" className="text-xs">
                  {selectedFeedIds.length} of {RSS_FEED_REGISTRY.length} selected
                </Badge>
              </div>
              <RSSFeedSelector
                selectedFeedIds={selectedFeedIds}
                onSelectionChange={setSelectedFeedIds}
              />
            </div>
            
            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium mb-2">Keywords to Monitor</label>
              <div className="flex flex-wrap gap-2">
                {selectedKeywords.map(keyword => (
                  <Badge key={keyword} variant="primary" className="cursor-pointer flex items-center gap-1">
                    {keyword}
                    <button
                      onClick={() => setSelectedKeywords(selectedKeywords.filter(k => k !== keyword))}
                      className="hover:text-red-600 text-sm"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
                <input
                  type="text"
                  placeholder="Add keyword..."
                  className="px-3 py-1 border rounded text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.currentTarget;
                      const keyword = input.value.trim();
                      if (keyword && !selectedKeywords.includes(keyword)) {
                        setSelectedKeywords([...selectedKeywords, keyword]);
                        input.value = '';
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Card>
      
      {/* Alert Carousels */}
      {selectedZone === 'usa' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* RSS News Alerts */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-yellow-600" />
                <h4 className="text-lg font-semibold">Industry News</h4>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setNewsCarouselIndex(prev => (prev - 1 + Math.max(1, rssItems.length)) % Math.max(1, rssItems.length))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-500">
                  {rssItems.length > 0 ? `${newsCarouselIndex + 1} / ${rssItems.length}` : '0 / 0'}
                </span>
                <Button variant="outline" size="sm" onClick={() => setNewsCarouselIndex(prev => (prev + 1) % Math.max(1, rssItems.length))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {loadingRSS ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : rssItems.length === 0 ? (
              <EmptyState
                icon={<Newspaper className="h-8 w-8" />}
                title="No RSS Items"
                description="No news items found matching your keywords"
              />
            ) : (
              <div className="space-y-3">
                {rssItems.slice(newsCarouselIndex, newsCarouselIndex + 3).map((item) => {
                  const feedSource = RSS_FEED_REGISTRY.find(f => item.source?.includes(f.name));
                  
                  return (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-semibold mb-1">{item.title}</h5>
                          <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge variant="primary" className="text-xs">
                              {feedSource?.name || item.source}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(item.pubDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => window.open(item.link, '_blank')}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          
          {/* Federal Register Alerts */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-blue-600" />
                <h4 className="text-lg font-semibold">Federal Register</h4>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setFederalCarouselIndex(prev => (prev - 1 + Math.max(1, federalAlerts.length)) % Math.max(1, federalAlerts.length))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-500">
                  {federalAlerts.length > 0 ? `${federalCarouselIndex + 1} / ${federalAlerts.length}` : '0 / 0'}
                </span>
                <Button variant="outline" size="sm" onClick={() => setFederalCarouselIndex(prev => (prev + 1) % Math.max(1, federalAlerts.length))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {loadingFederal ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : federalAlerts.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-8 w-8" />}
                title="No Federal Register Alerts"
                description="No documents found matching your keywords"
              />
            ) : (
              <div className="space-y-3">
                {federalAlerts.slice(federalCarouselIndex, federalCarouselIndex + 3).map((alert) => (
                  <div key={alert.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="primary" className="text-xs">{alert.type}</Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(alert.publication_date).toLocaleDateString()}
                          </span>
                        </div>
                        <h5 className="font-semibold mb-1">{alert.title}</h5>
                        {alert.summary && (
                          <p className="text-sm text-gray-600 line-clamp-2">{alert.summary}</p>
                        )}
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.open(alert.html_url, '_blank')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
      
      {/* Executive Orders List */}
      {selectedZone === 'usa' && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold">Executive Orders</h4>
            <div className="flex items-center gap-2">
              <select
                value={eoFilters.year}
                onChange={(e) => {
                  setEoFilters({ ...eoFilters, year: e.target.value });
                  setEoPage(1);
                }}
                className="px-2 py-1 text-sm border rounded"
              >
                <option value="2024">2024</option>
                <option value="2023">2023</option>
                <option value="2022">2022</option>
                <option value="2021">2021</option>
              </select>
              <select
                value={eoFilters.president}
                onChange={(e) => {
                  setEoFilters({ ...eoFilters, president: e.target.value });
                  setEoPage(1);
                }}
                className="px-2 py-1 text-sm border rounded"
              >
                <option value="all">All</option>
                <option value="joseph-r-biden">Biden</option>
                <option value="donald-trump">Trump</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-3">
            {loadingEOs ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : executiveOrders.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-8 w-8" />}
                title="No Executive Orders"
                description="No executive orders found matching your criteria"
              />
            ) : (
              <>
                {executiveOrders.slice((eoPage - 1) * 5, eoPage * 5).map(eo => (
                  <div key={eo.id} className="p-4 bg-gray-50 rounded-lg border hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="primary" className="text-xs">
                            EO {eo.executive_order_number || eo.document_number}
                          </Badge>
                          <Badge variant="primary" className="text-xs">{eo.president}</Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(eo.publication_date).toLocaleDateString()}
                          </span>
                        </div>
                        <h5 className="font-semibold mb-2 line-clamp-2">{eo.title}</h5>
                        {eo.summary && (
                          <p className="text-sm text-gray-600 line-clamp-3 mb-3">{eo.summary}</p>
                        )}
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.open(eo.html_url, '_blank')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Pagination */}
                {executiveOrders.length > 5 && (
                  <div className="flex items-center justify-between pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEoPage(Math.max(1, eoPage - 1))}
                      disabled={eoPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {eoPage} of {Math.ceil(executiveOrders.length / 5)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEoPage(Math.min(Math.ceil(executiveOrders.length / 5), eoPage + 1))}
                      disabled={eoPage >= Math.ceil(executiveOrders.length / 5)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      )}
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Newspaper className="h-8 w-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold">{rssItems.length}</div>
              <div className="text-sm text-gray-600">RSS Items</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-purple-600" />
            <div>
              <div className="text-2xl font-bold">{federalAlerts.length}</div>
              <div className="text-sm text-gray-600">Federal Alerts</div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Scale className="h-8 w-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold">{executiveOrders.length}</div>
              <div className="text-sm text-gray-600">Executive Orders</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

